import { FC, useEffect, useState } from "react";
import { ClockIcon } from "@heroicons/react/24/outline";

interface CountdownProps {
  expiryTime: string;
  onExpired?: () => void;
}

interface TimeLeft {
  minutes: number;
  seconds: number;
  isExpired: boolean;
}

const Countdown: FC<CountdownProps> = ({ expiryTime, onExpired }) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    minutes: 0,
    seconds: 0,
    isExpired: false,
  });

  useEffect(() => {
    const calculateTimeLeft = (): TimeLeft => {
      const expiryDate = new Date(expiryTime);
      const now = new Date();

      if (isNaN(expiryDate.getTime())) {
        return { minutes: 0, seconds: 0, isExpired: true };
      }

      const difference = expiryDate.getTime() - now.getTime();

      if (difference <= 0) {
        if (!timeLeft.isExpired && onExpired) {
          onExpired();
        }
        return { minutes: 0, seconds: 0, isExpired: true };
      }

      return {
        minutes: Math.floor((difference / (1000 * 60)) % 60),
        seconds: Math.floor((difference / 1000) % 60),
        isExpired: false,
      };
    };

    const initialTimeLeft = calculateTimeLeft();
    setTimeLeft(initialTimeLeft);

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [expiryTime, onExpired, timeLeft.isExpired]);

  if (timeLeft.isExpired) {
    return (
      <div className="flex items-center space-x-2 text-red-600">
        <ClockIcon className="w-5 h-5" />
        <span className="font-medium">Expired</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-2">
      <div className="flex items-center space-x-2 text-amber-600">
        <ClockIcon className="w-5 h-5" />
        <span className="font-medium">Time Remaining</span>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col items-center bg-amber-50 rounded-lg p-2">
          <span className="text-lg font-bold text-amber-700">{timeLeft.minutes}</span>
          <span className="text-xs font-medium text-amber-600">Minutes</span>
        </div>
        <div className="flex flex-col items-center bg-amber-50 rounded-lg p-2">
          <span className="text-lg font-bold text-amber-700">{timeLeft.seconds}</span>
          <span className="text-xs font-medium text-amber-600">Seconds</span>
        </div>
      </div>
    </div>
  );
};

export default Countdown;
